import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, UserPlus, Mail, Phone, Sun, Pencil, Check, X, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Customers directory — tabular view with inline email + phone editing.
 * Editing is gated to Super Admin & Sales (backend enforces the same rule).
 */
export default function Customers() {
    const [users, setUsers] = useState([]);
    const [plants, setPlants] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editId, setEditId] = useState(null);
    const [editEmail, setEditEmail] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const { user: me } = useAuth();
    const nav = useNavigate();

    const canEdit = me?.role === "super_admin" || me?.role === "sales";
    const isSuperAdmin = me?.role === "super_admin";

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const [u, p] = await Promise.all([
                api.get("/admin/users").then(r => r.data).catch(() => []),
                api.get("/plants").then(r => r.data),
            ]);
            setUsers(u);
            setPlants(p);
        } finally { setLoading(false); }
    }

    const customers = useMemo(() => users.filter(u => u.role === "customer"), [users]);
    const filtered = useMemo(() => customers.filter(u =>
        !q ||
        (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (u.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (u.phone || "").toLowerCase().includes(q.toLowerCase())
    ), [customers, q]);
    const plantById = useMemo(() => Object.fromEntries(plants.map(p => [p.id, p])), [plants]);

    function startEdit(c) {
        setEditId(c.id);
        setEditEmail(c.email || "");
        setEditPhone(c.phone || "");
    }
    function cancelEdit() {
        setEditId(null);
        setEditEmail("");
        setEditPhone("");
    }
    async function saveEdit(c) {
        // Guard against no-op saves.
        const changedEmail = editEmail.trim().toLowerCase() !== (c.email || "").toLowerCase();
        const changedPhone = editPhone.trim() !== (c.phone || "");
        if (!changedEmail && !changedPhone) { cancelEdit(); return; }
        setSaving(true);
        try {
            const payload = {};
            if (changedEmail) payload.email = editEmail.trim();
            if (changedPhone) payload.phone = editPhone.trim();
            const { data } = await api.patch(`/admin/users/${c.id}/contact`, payload);
            setUsers(users => users.map(u => u.id === c.id ? { ...u, ...data } : u));
            toast.success("Customer updated");
            cancelEdit();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update");
        } finally { setSaving(false); }
    }

    async function handleDelete(c) {
        if (!isSuperAdmin) return;
        if (c.email === "customer@rpplsolar.com") {
            toast.error("The Demo Customer is protected");
            return;
        }
        const plantCount = (c.allowed_station_ids || []).length;
        const confirmed = window.confirm(
            `Delete customer "${c.name || c.email}"?\n\n` +
            `This will permanently remove:\n` +
            `  • The customer's login (${c.email})\n` +
            `  • ${plantCount} plant registry mapping${plantCount === 1 ? "" : "s"}\n` +
            `  • Every service ticket, alert, document and contact tied to those plants\n\n` +
            `The underlying SolarOS station stays untouched so you can re-onboard it later with a fresh customer.\n\n` +
            `This action cannot be undone.`
        );
        if (!confirmed) return;
        setDeletingId(c.id);
        try {
            const { data } = await api.delete(`/admin/users/${c.id}`);
            toast.success(`Deleted ${data.email} · ${data.plant_count} plant${data.plant_count === 1 ? "" : "s"} removed`);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Delete failed";
            toast.error(detail);
        } finally { setDeletingId(null); }
    }

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Directory</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Customers ({customers.length})</h1>
                    <p className="text-sm text-[#6B7280] mt-1">
                        Customer accounts, contact info, and the plants they can access. {canEdit ? "Email & phone are editable inline." : "Ask a Super Admin or Sales to edit contact info."}
                    </p>
                </div>
                {isSuperAdmin && (
                    <Button
                        onClick={() => nav("/admin/users")}
                        className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10 px-5"
                        data-testid="invite-customer-btn"
                    >
                        <UserPlus className="w-4 h-4 mr-2" /> Invite Customer
                    </Button>
                )}
            </div>

            <div className="relative max-w-sm">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email, or phone…" className="pl-10 h-10 rounded-full bg-zinc-100 border-transparent focus-visible:ring-2 focus-visible:ring-[#0A0A0A]" data-testid="customers-search" />
            </div>

            {loading ? (
                <div className="text-sm text-[#6B7280]">Loading customer directory…</div>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
                    <Sun className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
                    <div className="text-sm font-medium text-[#111827]">No customers found</div>
                    <div className="text-xs text-[#6B7280] mt-1">Invite customers from the Admin console. They'll get view-only access to their own plants.</div>
                </div>
            ) : (
                <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden" data-testid="customers-table-wrap">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="customers-table">
                            <thead className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                                <tr>
                                    <Th>Customer Name</Th>
                                    <Th>Email ID</Th>
                                    <Th>Phone Number</Th>
                                    <Th right>Plants</Th>
                                    <Th>Assigned To</Th>
                                    {canEdit && <Th right>Actions</Th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((c, i) => {
                                    const assignedPlants = (c.allowed_station_ids || []).map(id => plantById[id]).filter(Boolean);
                                    const editing = editId === c.id;
                                    return (
                                        <tr key={c.id} className="border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#FAFAFA] transition-colors" data-testid={`customer-row-${i}`}>
                                            <td className="px-4 py-3 min-w-[180px]">
                                                <div className="font-medium text-[#111827] flex items-center gap-2" data-testid={`customer-name-${i}`}>
                                                    {c.name}
                                                    {c.email === "customer@rpplsolar.com" && (
                                                        <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#0A0A0A]/10 text-[#0A0A0A] border border-[#0A0A0A]/25">Demo</span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF] mt-0.5">
                                                    {c.status || "active"}
                                                </div>
                                            </td>

                                            {/* Email */}
                                            <td className="px-4 py-3 min-w-[240px]">
                                                {editing ? (
                                                    <Input
                                                        type="email"
                                                        value={editEmail}
                                                        onChange={e => setEditEmail(e.target.value)}
                                                        className="h-8 rounded-sm text-xs font-mono"
                                                        data-testid={`customer-email-input-${i}`}
                                                    />
                                                ) : (
                                                    <a href={`mailto:${c.email}`} className="text-xs text-[#374151] font-mono flex items-center gap-1.5 hover:text-[#0A0A0A]" data-testid={`customer-email-${i}`}>
                                                        <Mail className="w-3 h-3 flex-shrink-0" /> {c.email}
                                                    </a>
                                                )}
                                            </td>

                                            {/* Phone */}
                                            <td className="px-4 py-3 min-w-[180px]">
                                                {editing ? (
                                                    <Input
                                                        type="tel"
                                                        value={editPhone}
                                                        onChange={e => setEditPhone(e.target.value)}
                                                        placeholder="+91 …"
                                                        className="h-8 rounded-sm text-xs font-mono"
                                                        data-testid={`customer-phone-input-${i}`}
                                                    />
                                                ) : c.phone ? (
                                                    <a href={`tel:${c.phone}`} className="text-xs text-[#374151] font-mono flex items-center gap-1.5 hover:text-[#0A0A0A]" data-testid={`customer-phone-${i}`}>
                                                        <Phone className="w-3 h-3 flex-shrink-0" /> {c.phone}
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-[#9CA3AF] italic" data-testid={`customer-phone-${i}`}>Not set</span>
                                                )}
                                            </td>

                                            {/* Plants count */}
                                            <td className="px-4 py-3 text-right">
                                                <span className="inline-flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded-sm bg-[#F0FDF4] text-[#16A34A] border border-[#16A34A]/25" data-testid={`customer-plants-count-${i}`}>
                                                    <Sun className="w-3 h-3" />
                                                    {assignedPlants.length}
                                                </span>
                                            </td>

                                            {/* Assigned plants (compact chips) */}
                                            <td className="px-4 py-3 min-w-[200px]">
                                                {assignedPlants.length === 0 ? (
                                                    <span className="text-xs text-[#9CA3AF] italic">No plants assigned</span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-1">
                                                        {assignedPlants.slice(0, 3).map(p => (
                                                            <Link
                                                                key={p.id}
                                                                to={`/plants/${p.id}`}
                                                                className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] transition-colors"
                                                                data-testid={`customer-plant-${p.id}`}
                                                            >
                                                                <span className={`w-1.5 h-1.5 rounded-full ${p.health === "critical" ? "bg-red-500" : p.health === "warning" ? "bg-[#F97316]" : "bg-[#16A34A]"}`} />
                                                                {p.customer_name || p.name}
                                                            </Link>
                                                        ))}
                                                        {assignedPlants.length > 3 && (
                                                            <span className="text-[10px] text-[#6B7280]">+{assignedPlants.length - 3}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            {canEdit && (
                                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                                    {editing ? (
                                                        <div className="inline-flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={cancelEdit}
                                                                disabled={saving}
                                                                className="h-8 w-8 p-0 rounded-sm"
                                                                data-testid={`customer-cancel-${i}`}
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => saveEdit(c)}
                                                                disabled={saving}
                                                                className="h-8 rounded-sm bg-[#16A34A] hover:bg-[#15803D] text-white px-3"
                                                                data-testid={`customer-save-${i}`}
                                                            >
                                                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="inline-flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => startEdit(c)}
                                                                className="h-8 rounded-sm text-[#6B7280] hover:text-[#0A0A0A]"
                                                                data-testid={`customer-edit-${i}`}
                                                            >
                                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                                            </Button>
                                                            {isSuperAdmin && c.email !== "customer@rpplsolar.com" && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => handleDelete(c)}
                                                                    disabled={deletingId === c.id}
                                                                    className="h-8 rounded-sm text-[#6B7280] hover:text-red-600 hover:bg-red-50"
                                                                    data-testid={`customer-delete-${i}`}
                                                                >
                                                                    {deletingId === c.id ? (
                                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                    ) : (
                                                                        <><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</>
                                                                    )}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function Th({ children, right }) {
    return (
        <th className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-[#6B7280] ${right ? "text-right" : "text-left"}`}>
            {children}
        </th>
    );
}
